import twitterText from "twitter-text";
import extension from "./extension.json";
import registry from "./registry.json";
export const publisherRelease = extension;
export function compareVersions(a: string, b: string) {
	if (!/^\d+\.\d+\.\d+$/.test(a)) return -1;
	const left = a.split(".").map(Number),
		right = b.split(".").map(Number);
	for (let i = 0; i < Math.max(left.length, right.length); i++) {
		const difference = (left[i] ?? 0) - (right[i] ?? 0);
		if (difference) return Math.sign(difference);
	}
	return 0;
}
export type PublishingMethod = "browser" | "api" | "manual";
export interface Field {
	format?: "plain" | "markdown" | "rich_text";
	key: string;
	label: [string, string];
	kind: "text" | "textarea" | "select" | "url";
	required: boolean;
	required_when?: { field: string; value: string };
	max_length: number | null;
	min_length?: number;
	length_metric?: "twitter";
	options?: string[];
	option_labels?: Record<string, [string, string]>;
	transfer: "text" | "guided";
	selectors: string[];
	/**
	 * What this platform puts in the box when it is empty.
	 *
	 * The assistant refuses to overwrite a field that already holds something,
	 * so that a draft somebody started is never replaced silently. Some editors
	 * make that refusal permanent: CSDN's title box opens holding 【无标题】,
	 * and Toutiao's body is a ProseMirror whose placeholder is a real
	 * `<p>请输入正文</p>` node rather than CSS, so both read as occupied and
	 * neither could ever be filled.
	 *
	 * Listing the strings here keeps the refusal — a real draft still stops the
	 * write — while letting the platform's own placeholder out of the way. It is
	 * data rather than a heuristic because only the platform knows its wording,
	 * and a guess that got it wrong would overwrite somebody's work.
	 */
	empty_values?: string[];
	/**
	 * Whether this editor keeps the words and throws the markup away.
	 *
	 * A rich write is checked by counting the links that survived it, because a
	 * body whose citation back to the ondit page has become plain text is not the
	 * body that was sent. Toutiao and Baijiahao flatten every anchor, every time,
	 * so without this the assistant would warn on every fill — and a warning that
	 * is always there is one nobody reads. Set it only for an editor observed to
	 * flatten, and say so in the platform's instructions, so the person knows the
	 * links are gone rather than wondering why nothing was said.
	 */
	flattens_links?: boolean;
}
export interface MediaLimits {
	mime_types?: string[];
	max_bytes?: number;
	min_width?: number;
	max_width?: number;
	min_height?: number;
	max_height?: number;
	min_duration_ms?: number;
	max_duration_ms?: number;
	aspect_ratios?: [number, number][];
}
export interface MediaRule {
	limits?: Partial<Record<"image" | "video", MediaLimits>>;
	accept: string;
	required: boolean;
	multiple: boolean;
	transfer: string;
	max_count?: number;
	min_count?: number;
	/** A video has to be the only item. */
	video_alone?: boolean;
	no_video?: boolean;
}
export interface PlatformContract {
	id: string;
	name: string;
	methods: PublishingMethod[];
	/** The credential provider an `api` publish goes out through, connected on the Integrations screen. */
	credential_provider?: string;
	/** How an account for it is connected, which is the Integrations screen the panel deep-links to. */
	credential_kind?: "oauth" | "service_account";
	entry_url: string;
	hosts: string[];
	dynamic_host: boolean;
	publication_hosts?: string[];
	publication_custom_domain?: boolean;
	preview: {
		layout: string;
		fields: Field[];
		media: MediaRule | null;
	};
	browser: { verification: string; fill: string; final_action: string; instructions?: [string, string] };
	upstream_ref: string;
}
export interface PublishPayload {
	version: 1;
	platform: string;
	fields: Record<string, string>;
	media: { url: string; alt: string }[];
	source: { page_id: string; revision: number; canonical: string };
	target_url: string;
}
export interface BrowserTask {
	id: string;
	entity_id: string;
	payload: PublishPayload;
	state: string;
	published_url?: string | null;
	updated_at?: string;
	scheduled_at?: string | null;
	schedule_device?: string | null;
	schedule_account?: string | null;
	schedule_state?: string;
	schedule_error?: string | null;
}
export const platforms = registry as PlatformContract[];
export function platformById(id: string) {
	return platforms.find((p) => p.id === id);
}
/** The part of a brand's own platform (Settings → Publishing) a contract is drawn from. */
export interface CustomPlatformConfig {
	id: string;
	name: string;
	url: string;
	format: string;
	images: string;
	video: string;
	max_length: number | null;
}
/**
 * A brand's own platform as a guided browser contract.
 *
 * The server draws the same one (`platform_publishing::custom_contract`) and
 * pins its hash on a variant; the two have to agree on shape, not on bytes.
 */
export function customContract(config: CustomPlatformConfig): PlatformContract {
	let host = "";
	try {
		host = new URL(config.url).hostname.replace(/^www\./, "");
	} catch {}
	const layout = config.format === "social" || config.format === "video" ? config.format : "article";
	const accept = [config.images !== "no" && "image/*", config.video !== "no" && "video/*"].filter(Boolean);
	return {
		id: config.id,
		name: config.name,
		entry_url: config.url,
		hosts: [host],
		methods: ["browser"],
		dynamic_host: false,
		preview: {
			layout,
			fields: [
				{
					key: "title",
					label: ["Title", "标题"],
					kind: "text",
					required: layout !== "social",
					max_length: null,
					transfer: "guided",
					selectors: [],
				},
				{
					key: "body",
					label: ["Body", "正文"],
					kind: "textarea",
					required: true,
					max_length: config.max_length,
					transfer: "guided",
					selectors: [],
					format: "plain",
				},
			],
			media: accept.length ? { accept: accept.join(","), required: false, multiple: true, transfer: "guided" } : null,
		},
		browser: {
			verification: "needs_live_verification",
			fill: "assisted",
			final_action: "user",
			instructions: [
				"Open the platform, paste the prepared text into its editor, add any media, and publish there yourself.",
				"打开该平台，把准备好的内容粘贴到编辑器中，补充图片或视频后，由你在平台上完成发布。",
			],
		},
		upstream_ref: "custom",
	};
}
export function safeWebUrl(value: string) {
	try {
		const u = new URL(value);
		return u.protocol === "https:" && !u.username && !u.password;
	} catch {
		return false;
	}
}
export function allowedTarget(p: PlatformContract, value: string) {
	if (!safeWebUrl(value)) return false;
	const u = new URL(value);
	if (p.dynamic_host) return !["localhost", "127.0.0.1", "[::1]"].includes(u.hostname);
	return p.hosts.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`));
}
export function validPublishingSource(source: PublishPayload["source"]): boolean {
	if (!source || !Number.isInteger(source.revision) || source.revision < 1) return false;
	if (!source.page_id) return false;
	try {
		const url = new URL(source.canonical);
		return ["http:", "https:"].includes(url.protocol) && !!url.hostname && !url.username && !url.password;
	} catch {
		return false;
	}
}

/** `contract` is for a platform the registry does not hold, which a brand's own is. */
export function validatePayload(payload: PublishPayload, contract?: PlatformContract): string[] {
	const p = contract?.id === payload.platform ? contract : platformById(payload.platform);
	if (!p || payload.version !== 1) return ["platform"];
	const issues: string[] = [];
	if (!validPublishingSource(payload.source)) issues.push("source");
	if (!allowedTarget(p, payload.target_url)) issues.push("target_url");
	for (const f of p.preview.fields) {
		const v = payload.fields[f.key] ?? "";
		if (
			(fieldRequired(f, payload.fields) && !v.trim()) ||
			(f.max_length !== null && fieldLength(f, v) > f.max_length) ||
			(v && f.min_length !== undefined && fieldLength(f, v) < f.min_length) ||
			(v && f.kind === "url" && !safeWebUrl(v)) ||
			(v && f.options && !f.options.includes(v))
		)
			issues.push(f.key);
	}
	if (p.id === "youtube" && youtubeTagsLength(payload.fields.tags ?? "") > 500) issues.push("tags");
	if (payload.media.some((m) => !safeWebUrl(m.url)) || mediaBroken(p.preview.media, payload.media))
		issues.push("media");
	return issues;
}

/** Only an extension says a URL is a video; an address that does not say is never refused for it. */
export function isVideoUrl(value: string) {
	try {
		return /\.(mp4|mov|webm)$/.test(new URL(value).pathname.toLowerCase());
	} catch {
		return false;
	}
}

function mediaBroken(rule: MediaRule | null, media: PublishPayload["media"]) {
	if (!rule) return false;
	const n = media.length,
		video = media.some((m) => isVideoUrl(m.url));
	return (
		(rule.multiple === false && n > 1) ||
		(rule.max_count !== undefined && n > rule.max_count) ||
		// Media can be uploaded in the platform instead, which is why `required` is not enforced
		// either; a minimum only applies to what the handoff does carry.
		(rule.min_count !== undefined && n > 0 && n < rule.min_count) ||
		(!!rule.video_alone && video && n > 1) ||
		(!!rule.no_video && video)
	);
}

/** YouTube caps all tags together at 500 characters, and quotes a tag with a space in it. */
export function youtubeTagsLength(tags: string) {
	return tags
		.split(",")
		.map((t) => t.trim())
		.filter(Boolean)
		.reduce((sum, t) => sum + Array.from(t).length + (/\s/.test(t) ? 2 : 0), 0);
}

export function fieldRequired(field: Field, values: Record<string, string>) {
	return field.required || !!(field.required_when && values[field.required_when.field] === field.required_when.value);
}

export function allowedPublication(p: PlatformContract, value: string, target: string) {
	if (!safeWebUrl(value)) return false;
	const u = new URL(value);
	if (p.publication_custom_domain) return true;
	if (p.dynamic_host) return u.origin === new URL(target).origin;
	return [...p.hosts, ...(p.publication_hosts ?? [])].some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`));
}

export function fieldLength(field: Field, value: string) {
	return weightedLength(value, field.length_metric === "twitter");
}

/** X counts URLs and CJK by its own weights; everywhere else a character is a code point. */
export function weightedLength(value: string, twitter: boolean) {
	return twitter ? twitterText.parseTweet(value).weightedLength : Array.from(value).length;
}

/**
 * Platforms whose editor an unattended browser can drive to the end. LinkedIn left
 * on 2026-09-17: its rewritten feed hashes every class and its Post control carries
 * only a per-render key, so nothing stable identifies the button to click.
 */
export const UNATTENDED_PLATFORMS = ["youtube", "x", "devto"];

/** Fields that the browser cannot finish unattended must be resolved before a schedule is accepted. */
export function schedulingIssues(payload: PublishPayload): string[] {
	const p = platformById(payload.platform);
	if (!p || !UNATTENDED_PLATFORMS.includes(p.id)) return ["platform"];
	const issues = p.preview.fields
		.filter((f) => payload.fields[f.key]?.trim() && !f.selectors.length && f.kind !== "select")
		.map((f) => f.key);
	if (p.id === "youtube" && (payload.media.length !== 1 || payload.fields.visibility !== "public"))
		issues.push("media_or_visibility");
	if (p.id === "devto" && payload.media.length) issues.push("media");
	if (p.id === "devto" && (payload.fields.tags ?? "").split(/[,\s]+/).filter(Boolean).length > 4) issues.push("tags");
	if (p.id === "x" && payload.media.length > 4) issues.push("media");
	return issues;
}
